import * as React from 'react';
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../components/navigation/FalconFooter";
import {
    FalconNavigatorHeader,
    FalconPaymentRequest,
    ICustomerPortalService,
    Party,
    PaymentCard,
    PolicyProperty,
    PolicyPropertyEnum,
    ProductsEnum,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {CardPaymentPanel} from "../../../../components/payments/CardPaymentPanel";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {getAmountToDisplay, getProductByPolicyNumber, getSessionStorage} from "../../../../CommonFunctions";
import {FailedPaymentMessage} from "../../../../components/payments/FailedPaymentMessage";
import {Navigate} from "react-router-dom";

interface IExistingCardPaymentPageParams {
    policyNumber: string
    [key: string]: string | undefined
}


interface IState {
    party?: Party,
    settings: any,
    amount: number | null,
    paymentFailed: boolean,
    paymentCard?: PaymentCard,
    propertiesLoaded: boolean,
    directDebitSupported: boolean
}

@authorize
export class ExistingCardPaymentPagePlain extends React.Component<IWithRouterProps<IExistingCardPaymentPageParams>, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pluginTemplates = new PluginTemplates();

    constructor(props: IWithRouterProps<IExistingCardPaymentPageParams>) {
        super(props);

        this.state = {
            settings: getSessionStorage("paymentSettings"),
            amount: null,
            paymentFailed: false,
            propertiesLoaded: false,
            directDebitSupported: false
        };

        this.handleExistingCardPayment = this.handleExistingCardPayment.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Pay by card");

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output
            })
        });

        if (this.state.settings?.invoiceType) {
            this.customerPortalService.getPaymentAmount(this.state.settings?.invoiceType).subscribe((output: number) => {
                this.setState({
                    amount: output
                })
            });
        }

        this.customerPortalService.getMainPaymentCard(this.props.params.policyNumber).subscribe(
            (paymentCard: PaymentCard) => {
                this.setState({
                    paymentCard
                });
            }
        );

        this.customerPortalService.getPolicyPropertiesByPropertyKeys(this.props.params.policyNumber,
            [PolicyPropertyEnum.REUSE_MAIN_CARD, PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD, PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED]).subscribe(
            (properties: PolicyProperty[]) => {
                properties.forEach(property => {
                    if (property.key === PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED) {
                        this.setState({directDebitSupported: property.value === "true"});
                    }
                })
                this.setState({propertiesLoaded: true})
            }
        );

        if (this.props.params.policyNumber) {
            if (getProductByPolicyNumber(this.props.params.policyNumber) === ProductsEnum.HOME) {
                this.setState({
                    directDebitSupported: true
                });
            } else {
                this.customerPortalService.getPolicyPropertyByPropertyKey(this.props.params.policyNumber, PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED)
                    .subscribe(partyProperty => {
                        this.setState({
                            directDebitSupported: partyProperty?.value === "true"
                        });
                    });
            }
        }
    }

    public render(): React.ReactNode {
        const returnUrl = this.state.settings?.returnUrl || "/dashboard";
        const description = this.state.settings?.description || "Payment";

        if (this.state.paymentCard && this.state.party) {
            if (this.state.paymentCard.reuseCard) {
                return (
                    <div className="dashboard payment-details">
                        <FalconHeader/>
                        {this.state.paymentFailed && <FailedPaymentMessage directDebitSupported={this.state.directDebitSupported} />}

                        <div className="plugin">
                            <FalconNavigatorHeader
                                back={returnUrl}
                                title={description}
                            />

                            <div className="panel bg-white overflow-hidden p-0 mx-3 font-weight-extra-light">
                                <div className="inner-content pb-4">
                                    <div className="payoff-price">{getAmountToDisplay(this.state.amount)}</div>
                                    <p className="text-center">All figures include Insurance Premium Tax</p>
                                </div>
                            </div>

                            <div className="panel bg-white font-weight-extra-light">
                                <CardPaymentPanel amount={this.state.amount}
                                    party={this.state.party}
                                    handleExistingCardPayment={this.handleExistingCardPayment}
                                    newCardUrl={`/dashboard/account/newCardPayment/${this.props.params.policyNumber}`}
                                    mainCard={this.state.paymentCard}
                                />
                            </div>

                            {this.pluginTemplates.renderBackLink("Back", false, returnUrl)}
                        </div>

                        <FalconFooter/>
                    </div>
                )
            } else {
                return (
                    <Navigate to={{
                        pathname: `/dashboard/account/newCardPayment/${this.props.params.policyNumber}`,
                    }} />
                )
            }
        } else {
            return <Overlay show={true}/>
        }
    }

    private handleExistingCardPayment(event: any, cardId: number | undefined) : void {
        event.preventDefault();
        this.setState({paymentFailed: false});

        const request: FalconPaymentRequest = new FalconPaymentRequest();
        request.invoiceType = this.state.settings.invoiceType;
        request.cardId = cardId;
        request.product = getProductByPolicyNumber(this.props.params.policyNumber);

        this.customerPortalService.existingCardPayment(request).subscribe((response) => {
            if (response.success === true) {
                window.sessionStorage.setItem("showPopup", "true");
                window.sessionStorage.setItem("referrerJourney", "newPayment");
                window.sessionStorage.setItem("paymentAmount", getAmountToDisplay(this.state.amount));
                window.location.href = this.state.settings.returnUrl
            } else {
                this.setState({paymentFailed: true});
            }
        })
    }
}

export const ExistingCardPaymentPage = withRouter<{}, IExistingCardPaymentPageParams>(ExistingCardPaymentPagePlain)
